import { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '../store/authStore';
import * as profileApi from '../services/profileApi';
import type { UserProfileData } from '../services/profileApi';

// Flag to track if profile API is available (avoid repeated failed calls)
let profileApiAvailable = true;
let lastCheckTime = 0;
const CHECK_INTERVAL = 60000; // Re-check every 60 seconds

/**
 * Hook to fetch and manage the current user's profile
 * Gracefully handles missing API endpoints
 */
export function useProfile() {
  const { isAuthenticated, user } = useAuthStore();
  const [profile, setProfile] = useState<UserProfileData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Listen for profile-updated events to sync across components
  useEffect(() => {
    const handleProfileUpdated = () => {
      // Increment trigger to force re-fetch in all useProfile instances
      setRefreshTrigger(prev => prev + 1);
    };

    window.addEventListener('profile-updated', handleProfileUpdated);
    return () => {
      window.removeEventListener('profile-updated', handleProfileUpdated);
    };
  }, []);

  // Fetch profile when authenticated or when refresh is triggered
  useEffect(() => {
    if (isAuthenticated) {
      // Only try to load profile if API was available or enough time passed
      const now = Date.now();
      if (profileApiAvailable || now - lastCheckTime > CHECK_INTERVAL || refreshTrigger > 0) {
        profileApiAvailable = true; // Reset flag when manually triggered
        loadProfile();
      }
    } else {
      setProfile(null);
    }
  }, [isAuthenticated, refreshTrigger]);

  const loadProfile = async () => {
    // Don't set loading state to avoid blocking UI
    try {
      const data = await profileApi.getProfile();
      if (mountedRef.current) {
        setProfile(data);
        profileApiAvailable = true;
      }
    } catch (err: any) {
      // Mark API as unavailable to avoid repeated failed calls
      profileApiAvailable = false;
      lastCheckTime = Date.now();
      // Silently fail - don't block the app
      console.warn('Profile API not available:', err.message);
      if (mountedRef.current) {
        setProfile(null);
      }
    }
  };

  const updateProfile = async (data: Parameters<typeof profileApi.updateProfile>[0]) => {
    try {
      const updated = await profileApi.updateProfile(data);
      setProfile(updated);
      return updated;
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const refetch = () => {
    if (isAuthenticated) {
      profileApiAvailable = true; // Reset flag to retry
      loadProfile();
    }
  };

  // Get display name with fallback to authStore user data
  const displayName = profile?.display_name
    || profile?.username
    || profile?.email?.split('@')[0]
    || user?.displayName
    || user?.username
    || user?.email?.split('@')[0]
    || null;

  // Get first name (first word of display_name)
  const firstName = displayName ? displayName.split(' ')[0] : null;

  // Get avatar URL with fallback to authStore user data
  const avatarUrl = profile?.avatar_url || user?.avatarUrl || null;

  return {
    profile,
    isLoading,
    error,
    updateProfile,
    refetch,
    displayName,
    firstName,
    avatarUrl,
  };
}
